import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInvoiceStore, useInvoiceCalculations } from '../stores/invoiceStore';
import type { LineItem } from '@dutchinvoice/types';
import { useNumberField, usePlaceholderField } from '../hooks/useFormFields';
import { Trash2, Plus, Percent, Coins, Copy, Check } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useClickOutside } from '../hooks/useClickOutside';
import { moneyUtils, useMoneyDisplay } from '../utils/money';
import { useInputHandling } from '../hooks/useInputHandling';

interface LineItemRowProps {
  item: LineItem;
  isEditing: boolean;
  onStartEdit: () => void;
  onFinishEdit: () => void;
  onRemove: (id: string) => void;
  isInvoiceEditable: boolean;
}

const LineItemRow: React.FC<LineItemRowProps> = ({ 
  item, 
  isEditing, 
  onStartEdit, 
  onFinishEdit, 
  onRemove,
  isInvoiceEditable
}) => {
  const { t } = useTranslation();
  const { updateLineItem, duplicateLineItem } = useInvoiceStore();
  const { formatMoney, formatCurrency, formatNumber } = useMoneyDisplay();
  const editRef = useRef<HTMLDivElement>(null);
  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const [hasChanges, setHasChanges] = React.useState(false);

  // Focus description input when editing starts and item has placeholder description
  React.useEffect(() => {
    if (isEditing && item.isPlaceholder?.description && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
  }, [isEditing, item.isPlaceholder?.description]);

  useClickOutside(editRef, () => {
    if (isEditing) {
      if (!hasChanges && item.isPlaceholder?.description && item.isPlaceholder?.quantity && item.isPlaceholder?.price) {
        onRemove(item.id);
      } else {
        onFinishEdit();
      }
    }
  });

  const handleFieldChange = (field: keyof Omit<LineItem, 'id'>, value: string) => {
    setHasChanges(true);
    console.log('[LineItemRow] handleFieldChange', { field, value, itemId: item.id });
    updateLineItem(item.id, field, value);
  };

  const descriptionField = usePlaceholderField({
    initialValue: item.description,
    isPlaceholder: item.isPlaceholder?.description,
    onUpdate: (value) => handleFieldChange('description', value)
  });

  const quantityField = useNumberField({
    initialValue: item.quantity,
    isPlaceholder: item.isPlaceholder?.quantity,
    onUpdate: (value) => handleFieldChange('quantity', value)
  });

  const priceField = useNumberField({
    initialValue: formatMoney(item.price),
    isPlaceholder: item.isPlaceholder?.price,
    onUpdate: (value) => handleFieldChange('price', value),
    isPrice: true
  });

  const vatRateField = useNumberField({
    initialValue: item.vatRate,
    isPlaceholder: item.isPlaceholder?.vatRate,
    onUpdate: (value) => handleFieldChange('vatRate', value)
  });

  const { inputProps: descriptionInputProps, handleKeyDown } = useInputHandling({
    onSave: onFinishEdit,
    isEditing,
    value: descriptionField.value,
    onChange: descriptionField.handleChange,
    onFocus: descriptionField.handleFocus,
    onBlur: descriptionField.handleBlur
  });

  const { inputProps: quantityInputProps } = useInputHandling({
    onSave: onFinishEdit,
    isEditing,
    value: quantityField.value,
    onChange: quantityField.handleChange,
    onFocus: quantityField.handleFocus,
    onBlur: quantityField.handleBlur
  });

  const { inputProps: priceInputProps } = useInputHandling({
    onSave: onFinishEdit,
    isEditing,
    value: priceField.value,
    onChange: priceField.handleChange,
    onFocus: priceField.handleFocus,
    onBlur: priceField.handleBlur
  });

  const { inputProps: vatRateInputProps } = useInputHandling({
    onSave: onFinishEdit,
    isEditing,
    value: vatRateField.value,
    onChange: vatRateField.handleChange,
    onFocus: vatRateField.handleFocus,
    onBlur: vatRateField.handleBlur
  });

  if (isEditing) {
    return (
      <motion.tr key={item.id} layout>
        <td colSpan={6} className="p-0">
          <motion.div
            ref={editRef}
            initial={{ opacity: 0, y: -2 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -2 }}
            className="relative z-10"
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            {/* Background that extends beyond container */}
            <div className="absolute -inset-x-2 md:-inset-x-4 right-[-0.5rem] md:right-[-1rem] -inset-y-2 bg-white shadow-lg rounded-xl border border-gray-100" />
            
            <div className="relative flex w-full items-center h-9">
              {/* Quantity */}
              <div className="w-16 md:w-24 px-2 md:px-4">
                <input
                  {...quantityInputProps}
                  placeholder={quantityField.placeholder}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm text-right pr-1 md:pr-2 translate-x-1 md:translate-x-2 h-9"
                />
              </div>

              {/* Description */}
              <div className="w-48 md:flex-1 px-2 md:px-4">
                <input
                  {...descriptionInputProps}
                  ref={descriptionInputRef}
                  placeholder={descriptionField.placeholder}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm -translate-x-1 md:-translate-x-2 h-9"
                />
              </div>

              {/* Price */}
              <div className="w-24 md:w-32 px-2 md:px-4">
                <input
                  {...priceInputProps}
                  placeholder={priceField.placeholder}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm text-right pr-1 md:pr-2 translate-x-1 md:translate-x-2 h-9"
                />
              </div>

              {/* VAT Rate */}
              <div className="w-16 md:w-24 px-2 md:px-4">
                <div className="relative translate-x-1 md:translate-x-2">
                  <input
                    {...vatRateInputProps}
                    placeholder={vatRateField.placeholder}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm text-right pr-4 md:pr-6 h-9"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-1 md:pr-2 pointer-events-none">
                    <span className="text-gray-500">%</span>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="w-24 md:w-32 px-2 md:px-4 flex items-center justify-end">
                <button
                  onClick={onFinishEdit}
                  className="btn-subtle btn-xs rounded-full shadow-sm hover:shadow-md active:shadow-sm transition-all duration-200 p-2 hover:bg-white"
                >
                  <span className="sr-only">{t('actions.save')}</span>
                  <Check className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </motion.div>
        </td>
      </motion.tr>
    );
  }

  const itemTotal = moneyUtils.multiply(
    moneyUtils.toUniversal(item.price),
    parseFloat(item.quantity) || 0
  );

  return (
    <motion.tr
      key={item.id}
      layout
      className={`group relative transition-all duration-200 overflow-visible hover:z-10 ${
        isInvoiceEditable ? 'cursor-pointer' : 'cursor-not-allowed pointer-events-none'
      }`}
      onClick={isInvoiceEditable ? onStartEdit : undefined}
    >
      <td colSpan={6} className="p-0">
        <div className="relative flex items-center h-9">
          <div className="absolute -inset-x-2 md:-inset-x-4 right-[-3rem] md:right-[-6rem] -inset-y-1.5 transition-all duration-200 bg-transparent group-hover:bg-white/80 rounded-xl shadow-[0_0_0_rgba(0,0,0,0)] group-hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]" />
          
          <div className="flex flex-1 items-center relative">
            <div className="w-16 md:w-24 px-2 md:px-4 text-sm text-right transition-transform duration-200 group-hover:scale-[1.03] origin-right">
              <span className={`transition-colors duration-200 ${item.isPlaceholder?.quantity ? "text-gray-400 italic" : "text-gray-900 group-hover:text-gray-950"}`}>
                {formatNumber(item.quantity)}
              </span>
            </div>
            <div className="w-48 md:flex-1 px-2 md:px-4 text-sm transition-transform duration-200 group-hover:scale-[1.03] origin-left">
              <span className={`transition-colors duration-200 ${item.isPlaceholder?.description ? "text-gray-400 italic" : "text-gray-900 group-hover:text-gray-950"}`}>
                {item.description}
              </span>
            </div>
            <div className="w-24 md:w-32 px-2 md:px-4 text-sm text-right transition-transform duration-200 group-hover:scale-[1.03] origin-right">
              <span className={`transition-colors duration-200 ${item.isPlaceholder?.price ? "text-gray-400 italic" : "text-gray-900 group-hover:text-gray-950"}`}>
                {formatCurrency(item.price)}
              </span>
            </div>
            <div className="w-16 md:w-24 px-2 md:px-4 text-sm text-right transition-transform duration-200 group-hover:scale-[1.03] origin-right">
              <span className="transition-colors duration-200 text-gray-900 group-hover:text-gray-950">{formatNumber(item.vatRate)}%</span>
            </div>
            <div className="w-24 md:w-32 px-2 md:px-4 text-sm text-right transition-transform duration-200 group-hover:scale-[1.03] origin-right">
              <span className="transition-colors duration-200 font-medium text-gray-900 group-hover:text-gray-950">
                {formatCurrency(itemTotal)}
              </span>
            </div>
          </div>

          {isInvoiceEditable && (
            <div className="absolute -right-12 md:-right-20 flex items-center gap-2 py-3 md:opacity-0 md:group-hover:opacity-100 transition-all duration-200 
              md:translate-x-4 md:group-hover:translate-x-0 
              touch:opacity-100 touch:translate-x-0"
              data-pdf-exclude="true"
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  duplicateLineItem(item.id);
                }}
                className="btn-subtle btn-xs rounded-full shadow-sm hover:shadow-md active:shadow-sm transition-all duration-200 p-2 hover:bg-white"
                aria-label={t('actions.copy')}
                data-pdf-exclude="true"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemove(item.id);
                }}
                className="btn-danger btn-xs rounded-full shadow-sm hover:shadow-md active:shadow-sm transition-all duration-200 p-2 hover:bg-white"
                aria-label={t('invoice.lineItems.remove')}
                data-pdf-exclude="true"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </td>
    </motion.tr>
  );
};

const InvoiceLineItems: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useMoneyDisplay();
  const currentInvoice = useInvoiceStore((state) => state.currentInvoice);
  const items = useInvoiceStore((state) => state.currentInvoice.items);
  const editingSection = useInvoiceStore((state) => state.editingSection);
  const { addLineItem, removeLineItem, setEditingSection, updateDiscount } = useInvoiceStore();
  const { subtotal, vat, total, discountAmount } = useInvoiceCalculations();
  const discount = useInvoiceStore((state) => state.currentInvoice.discount);
  const isEditingDiscount = editingSection?.type === 'discount';
  
  // Check if invoice is editable based on its status and payment status
  const isInvoiceEditable = currentInvoice.status !== 'sent' && currentInvoice.paymentStatus !== 'paid';

  const handleDiscountChange = React.useCallback((type: typeof discount.type) => {
    updateDiscount({
      type,
      value: type === 'none' ? '0' : discount.value,
      isPlaceholder: false
    });
    if (type === 'none') {
      setEditingSection(null);
    }
  }, [updateDiscount, discount.value, setEditingSection]);

  const handleValueChange = React.useCallback((value: string) => {
    updateDiscount({
      type: discount.type,
      value,
      isPlaceholder: false
    });
  }, [updateDiscount, discount.type]);

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="overflow-visible md:overflow-visible overflow-x-auto relative z-0">
        <table className="min-w-full md:table-fixed w-max md:w-full">
          <colgroup>
            <col className="w-16 md:w-24" />{/* Quantity */}
            <col className="w-48 md:w-auto" />{/* Description - takes remaining space */}
            <col className="w-24 md:w-32" />{/* Price */}
            <col className="w-16 md:w-24" />{/* VAT Rate */}
            <col className="w-24 md:w-32" />{/* Amount */}
            <col className="w-0" />{/* Actions - zero width */}
          </colgroup>
          <thead>
            <tr className="border-b border-gray-200">
              <th scope="col" className="px-2 md:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('invoice.lineItems.quantity')}
              </th>
              <th scope="col" className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('invoice.lineItems.description')}
              </th>
              <th scope="col" className="px-2 md:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('invoice.lineItems.price')}
              </th>
              <th scope="col" className="px-2 md:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('invoice.lineItems.vatRate')}
              </th>
              <th scope="col" className="px-2 md:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('invoice.lineItems.amount')}
              </th>
              <th scope="col" className="w-0 p-0" data-pdf-exclude="true">
                <span className="sr-only">{t('actions.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <AnimatePresence mode="popLayout">
              {items.map((item) => (
                <LineItemRow
                  key={item.id}
                  item={item}
                  isEditing={editingSection?.type === 'item' && editingSection.itemId === item.id}
                  onStartEdit={() => setEditingSection({ type: 'item', itemId: item.id })}
                  onFinishEdit={() => setEditingSection(null)}
                  onRemove={removeLineItem}
                  isInvoiceEditable={isInvoiceEditable}
                />
              ))}
            </AnimatePresence>
          </tbody>
          <tfoot>
            <tr data-pdf-exclude="true">
              <td colSpan={6} className="px-2 md:px-4 py-4 text-right">
                {isInvoiceEditable ? (
                  <button
                    onClick={() => addLineItem()}
                    className="inline-flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700 font-medium add-item-button"
                    data-pdf-exclude="true"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{t('invoice.lineItems.add')}</span>
                  </button>
                ) : null}
              </td>
            </tr>
            <tr className="border-t border-gray-200">
              <td colSpan={4} className="px-2 md:px-4 py-3 text-sm text-right font-medium text-gray-500">
                {t('invoice.subtotal')}
              </td>
              <td colSpan={2} className="px-2 md:px-4 py-3 text-right text-sm font-medium">
                {formatCurrency(subtotal)}
              </td>
            </tr>
            {parseFloat(discountAmount) > 0 && (
              <tr>
                <td colSpan={4} className="px-2 md:px-4 py-3 text-sm text-right font-medium text-gray-500">
                  {t('invoice.discount.label')}
                </td>
                <td colSpan={2} className="px-2 md:px-4 py-3 text-right text-sm font-medium text-primary-600">
                  <span>-{formatCurrency(discountAmount)}</span>
                </td>
              </tr>
            )}
            <tr>
              <td colSpan={4} className="px-2 md:px-4 py-3 text-sm text-right font-medium text-gray-500">
                {t('invoice.vat')}
              </td>
              <td colSpan={2} className="px-2 md:px-4 py-3 text-right text-sm font-medium">
                {formatCurrency(vat)}
              </td>
            </tr>
            <tr className="border-t border-gray-200">
              <td colSpan={4} className="px-2 md:px-4 py-4 text-base text-right font-semibold text-gray-900">
                {t('invoice.total')}
              </td>
              <td colSpan={2} className="px-2 md:px-4 py-4 text-right text-lg font-bold whitespace-nowrap">
                {formatCurrency(total)}
              </td>
            </tr>
            <tr data-pdf-exclude="true">
              <td colSpan={6} className="px-2 md:px-4 py-2">
                {isInvoiceEditable && (
                  <div className="min-h-[2.5rem] relative">
                    <AnimatePresence mode="wait">
                      {isEditingDiscount ? (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="absolute inset-0 flex items-center justify-end space-x-4"
                          data-pdf-exclude="true"
                        >
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDiscountChange('percentage')}
                              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm transition-all duration-200 ${
                                discount.type === 'percentage'
                                  ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-300 shadow-sm'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-primary-600'
                              }`}
                            >
                              <Percent className="w-3.5 h-3.5" />
                              <span>{t('invoice.discount.percentage')}</span>
                            </button>
                            <button
                              onClick={() => handleDiscountChange('fixed')}
                              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm transition-all duration-200 ${
                                discount.type === 'fixed'
                                  ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-300 shadow-sm'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-primary-600'
                              }`}
                            >
                              <Coins className="w-3.5 h-3.5" />
                              <span>{t('invoice.discount.fixed')}</span>
                            </button>
                            <button
                              onClick={() => handleDiscountChange('none')}
                              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm transition-all duration-200 ${
                                discount.type === 'none'
                                  ? 'bg-gray-100 text-gray-700'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <span>{t('invoice.discount.none')}</span>
                            </button>
                          </div>
                          {discount.type !== 'none' && (
                            <div className="relative w-32">
                              <input
                                type="text"
                                value={discount.value}
                                onChange={(e) => handleValueChange(e.target.value)}
                                className="block w-full rounded-lg border-gray-200 bg-white/50 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm text-right pr-6 transition-all duration-200 hover:border-primary-300"
                                placeholder={discount.type === 'percentage' ? '10' : '50,00'}
                              />
                              <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm">
                                  {discount.type === 'percentage' ? '%' : '€'}
                                </span>
                              </div>
                            </div>
                          )}
                          <button
                            onClick={() => setEditingSection(null)}
                            className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                          >
                            {t('actions.done')}
                          </button>
                        </motion.div>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="absolute inset-0 flex items-center justify-end space-x-1 group cursor-pointer add-discount-button"
                          onClick={() => setEditingSection({ type: 'discount' })}
                          data-pdf-exclude="true"
                        >
                          <button className="text-sm text-gray-500 group-hover:text-primary-600 transition-colors duration-200">
                            {discount.type === 'none' ? t('invoice.discount.add') : t('invoice.discount.edit')}
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default InvoiceLineItems; 